import { useEffect, useState } from "react";
import { AlertTriangle, FileText, Loader2 } from "lucide-react";
import { PreviewToolbar } from "./PreviewToolbar";
import { PREVIEW_ETA_MS, type PreviewStatus } from "./usePreviewIntegration";
import { VwrPreview } from "./VwrPreview";

interface PreviewPaneProps {
  status: PreviewStatus;
  isStale: boolean;
  previewUrl: string | null;
  generatedAt: Date | null;
  idleSecondsRemaining: number | null;
  error: string | null;
  pageInfo?: string;
}

export function PreviewPane({
  status,
  isStale,
  previewUrl,
  generatedAt,
  idleSecondsRemaining,
  error,
  pageInfo,
}: PreviewPaneProps) {
  // Render-progress timer. Drives the progress bar / countdown shown
  // while the DAG run is in flight.  Resets every time we transition
  // into 'generating'.
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (status !== "generating") {
      setElapsedMs(0);
      return;
    }
    const start = Date.now();
    setElapsedMs(0);
    const id = window.setInterval(() => {
      setElapsedMs(Date.now() - start);
    }, 250);
    return () => window.clearInterval(id);
  }, [status]);

  const progress = Math.min(elapsedMs / PREVIEW_ETA_MS, 1);
  const remainingS = Math.max(
    Math.round((PREVIEW_ETA_MS - elapsedMs) / 1000),
    0,
  );
  const overtime = elapsedMs >= PREVIEW_ETA_MS;

  return (
    <div
      className="hair-l border-l overflow-hidden flex flex-col h-full"
      style={{
        background:
          "radial-gradient(circle at 30% 20%, #DCE4EF 0%, #CED9E8 70%)",
      }}
    >
      <PreviewToolbar
        status={status}
        isStale={isStale}
        generatedAt={generatedAt}
        idleSecondsRemaining={idleSecondsRemaining}
        pageInfo={pageInfo}
      />

      {isStale && status !== "generating" && previewUrl && (
        <div className="mx-6 mt-4 px-3 py-2.5 rounded-md bg-spark-s/50 hair border border-spark-s text-[11.5px] text-ink-2 leading-snug flex items-start gap-2">
          <AlertTriangle
            size={13}
            strokeWidth={2}
            className="flex-shrink-0 mt-0.5 text-spark"
          />
          <div>
            You've edited fields since the last preview.
            <span className="block text-ink-4 mt-0.5">
              Click "Refresh preview" to enable Save
              {idleSecondsRemaining != null && idleSecondsRemaining > 0 && (
                <>
                  {" "}— or wait <span className="num">{idleSecondsRemaining}s</span>{" "}
                  for auto-refresh
                </>
              )}
              .
            </span>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col">
        {status === "generating" && (
          <div className="flex-1 min-h-0 overflow-auto px-12 pt-12 pb-12 flex items-start justify-center">
            <div className="bg-paper-hi rounded-lg shadow-card px-6 py-5 max-w-sm w-full">
              <div className="flex items-center gap-2 mb-3">
                <Loader2
                  size={15}
                  strokeWidth={1.8}
                  className="animate-spin text-tide flex-shrink-0"
                />
                <div className="text-[13.5px] font-medium">
                  Rendering preview…
                </div>
                <div className="ml-auto text-[11.5px] text-ink-4 num">
                  {overtime
                    ? "still working"
                    : `~${remainingS}s remaining`}
                </div>
              </div>
              <div
                className="h-1.5 rounded-full bg-canvas-2 overflow-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={Math.round(PREVIEW_ETA_MS / 1000)}
                aria-valuenow={Math.round(elapsedMs / 1000)}
              >
                <div
                  className={`h-full ${
                    overtime ? "bg-spark pulse-soft" : "bg-tide"
                  } transition-[width] duration-200 ease-linear`}
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
              <div className="text-[11px] text-ink-4 mt-2">
                {overtime
                  ? "Taking longer than usual — most renders finish in about a minute."
                  : "Rendering usually takes about a minute."}
              </div>
            </div>
          </div>
        )}

        {status !== "generating" && status === "failed" && (
          <div className="flex-1 min-h-0 overflow-auto px-12 pt-12 pb-12 flex items-start justify-center">
            <div className="bg-paper-hi rounded-lg shadow-card p-6 max-w-md text-center">
              <div className="text-[14px] font-medium mb-1">Preview failed</div>
              <div className="text-[12.5px] text-ink-4">
                {error ?? "Unknown error"}
              </div>
            </div>
          </div>
        )}

        {status !== "generating" &&
          status !== "failed" &&
          (previewUrl ? (
            <VwrPreview url={previewUrl} className="flex-1 min-h-0 w-full" />
          ) : (
            <div className="flex-1 min-h-0 overflow-auto px-12 pt-12 pb-12 flex items-start justify-center">
              <div className="bg-paper-hi rounded-lg shadow-card p-8 max-w-md text-center">
                <FileText
                  size={28}
                  strokeWidth={1.5}
                  className="mx-auto mb-3 text-ink-4"
                />
                <div className="text-[14px] font-medium mb-1">
                  No preview yet
                </div>
                <div className="text-[12.5px] text-ink-4">
                  Click "Refresh preview" in the header to render one.
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
